import {
  KYDAdminEvent,
  KYDAdminEventDetail,
} from "@common/interfaces/KYDEvent";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDialog } from "../../../Common/Dialog";
import { useLoading } from "../../hooks/form";
import actions from "../../../actions";

const eventCache = new Map<string, KYDAdminEventDetail>();

export const clearKydEventCache = () => {
  eventCache.clear();
};

const mergeSectionResponseIntoEvent = (
  event: KYDAdminEventDetail,
  sectionResponse: Record<string, any>
) => {
  const updatedByType = Object.values(sectionResponse || {}).reduce(
    (acc, section: any) => {
      if (section?.type) {
        acc[section.type] = section;
      }
      return acc;
    },
    {} as Record<string, any>
  );

  return {
    ...event,
    nav: (event.nav || []).map((nav) => ({
      ...nav,
      sections: (nav.sections || []).map(
        (section) => updatedByType[section.type] || section
      ),
    })),
  } as KYDAdminEventDetail;
};

export const useKydEvent = (
  event_id?: string,
  config?: { sectionTypes?: string[] }
) => {
  const [kydEvent, setKydEvent] = useState<KYDAdminEventDetail | null>();
  const [loading, setLoading] = useLoading<{ init: boolean }>({
    init: false,
  });
  const dialog = useDialog();
  const defaultSectionTypesKey = (config?.sectionTypes || []).join(",");
  const defaultSectionTypes = useMemo(
    () =>
      defaultSectionTypesKey ? defaultSectionTypesKey.split(",") : undefined,
    [defaultSectionTypesKey]
  );

  const fetchEvent = useCallback(
    async (eventId?: string, options?: any): Promise<KYDAdminEvent> => {
      if (!options || (options && !options.silent)) {
        setLoading("init", true);
      }

      let _;
      try {
        if (!eventId) {
          throw Error("NO event ID provided");
        }
        const requestedSectionTypes =
          options?.sectionTypes || defaultSectionTypes;
        const cachedEvent = eventCache.get(eventId);

        if (
          Array.isArray(requestedSectionTypes) &&
          requestedSectionTypes.length > 0
        ) {
          const baseEvent =
            cachedEvent ||
            ((await actions.ADMIN.fetchEvent(eventId)) as KYDAdminEventDetail);

          if (baseEvent) {
            const sectionResponse = await actions.ADMIN.fetchEventSections(
              eventId,
              requestedSectionTypes
            );
            _ = mergeSectionResponseIntoEvent(baseEvent, sectionResponse);
          } else {
            _ = baseEvent;
          }
        } else {
          _ = await actions.ADMIN.fetchEvent(eventId);
        }

        if (_?.id) {
          eventCache.set(eventId, _ as KYDAdminEventDetail);
        }
        setKydEvent(_);
      } catch (err) {
        dialog({ text: err.message });
      }
      setLoading("init", false);
      return _;
    },
    [setLoading, dialog, defaultSectionTypesKey]
  );

  useEffect(() => {
    if (event_id) {
      setLoading("init", true);

      fetchEvent(event_id).finally(() => {
        setLoading("init", false);
      });
    }
  }, [event_id, fetchEvent, setLoading]);

  return {
    refetch: (options?: any) => {
      const incomingEvent = options?.event;
      if (incomingEvent?.id) {
        const eventKey = event_id || incomingEvent.id;
        const cachedEvent = eventCache.get(eventKey);
        const nextEvent =
          cachedEvent && !incomingEvent.nav
            ? ({ ...cachedEvent, ...incomingEvent } as KYDAdminEventDetail)
            : (incomingEvent as KYDAdminEventDetail);

        eventCache.set(eventKey, nextEvent);
        setKydEvent(nextEvent);
        return Promise.resolve(nextEvent);
      }

      return fetchEvent(event_id, {
        ...options,
        sectionTypes: options?.sectionTypes || defaultSectionTypes,
      });
    },
    kydEvent,
    loading: loading.init as boolean,
  };
};
